import { useRef, useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'

interface CameraInlineProps {
  onCapture: (blob: Blob) => void
  onClose: () => void
}

/**
 * CameraInline — viewfinder com captura rápida via ImageCapture API.
 * Toque = foto instantânea (sem preview/OK), câmera continua aberta.
 * Fallback: navegadores sem ImageCapture usam canvas.drawImage do <video>.
 */
export default function CameraInline({ onCapture, onClose }: CameraInlineProps) {
  const videoRef    = useRef<HTMLVideoElement>(null)
  const streamRef   = useRef<MediaStream | null>(null)
  const captureRef  = useRef<ImageCapture | null>(null)   // ImageCapture instance
  const canvasRef   = useRef<HTMLCanvasElement | null>(null)   // fallback canvas
  const [ready,     setReady]    = useState(false)
  const [flashing,  setFlashing] = useState(false)
  const [count,     setCount]    = useState(0)
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment')

  // ── Start camera stream ──
  const startStream = useCallback(async (facing: string) => {
    // Limpa stream anterior
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
    }
    setReady(false)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width:  { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      })
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }

      // ImageCapture API (máxima resolução fotográfica)
      const track = stream.getVideoTracks()[0]
      if (typeof ImageCapture !== 'undefined') {
        captureRef.current = new ImageCapture(track)
      } else {
        captureRef.current = null
      }

      setReady(true)
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        toast.error('Permissão de câmera negada. Habilite nas configurações.')
      } else {
        toast.error('Não foi possível acessar a câmera')
      }
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    startStream(facingMode)
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trocar câmera ──
  const flipCamera = useCallback(() => {
    const next = facingMode === 'environment' ? 'user' : 'environment'
    setFacingMode(next)
    startStream(next)
  }, [facingMode, startStream])

  // ── Capturar foto ──
  const takePhoto = useCallback(async () => {
    if (!ready) return

    // Flash visual
    setFlashing(true)
    setTimeout(() => setFlashing(false), 120)

    try {
      let blob

      if (captureRef.current) {
        // ImageCapture API — resolução máxima
        blob = await captureRef.current.takePhoto()
      } else {
        // Fallback: canvas do vídeo
        const video = videoRef.current
        if (!video) return
        const canvas = canvasRef.current || document.createElement('canvas')
        canvasRef.current = canvas
        canvas.width  = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92))
      }

      if (blob) {
        setCount(c => c + 1)
        onCapture(blob)
      }
    } catch (err) {
      console.error('Erro na captura:', err)
      toast.error('Erro ao capturar foto')
    }
  }, [ready, onCapture])

  return (
    <div className="fixed inset-0 z-[310] flex flex-col" style={{ background: '#000' }}>
      {/* Viewfinder */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="w-full h-full object-cover"
          style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
        />

        {/* Flash overlay */}
        {flashing && (
          <div className="absolute inset-0 bg-white/30 pointer-events-none" />
        )}

        {/* Contador de fotos */}
        {count > 0 && (
          <div
            className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
          >
            <span className="material-symbols-outlined text-[#f0f0f0] text-sm">photo_camera</span>
            <span className="text-[13px] font-semibold text-[#f0f0f0] [font-variant-numeric:tabular-nums]">{count}</span>
          </div>
        )}

        {/* Loading */}
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <span className="text-[13px] text-[#666]">Iniciando câmera...</span>
          </div>
        )}
      </div>

      {/* Toolbar inferior */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-6 py-4"
        style={{ background: 'rgba(0,0,0,0.9)', paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        {/* Fechar */}
        <button
          onClick={onClose}
          className="w-11 h-11 flex items-center justify-center rounded-full border-none cursor-pointer bg-white/[0.08]"
          aria-label="Fechar câmera"
        >
          <span className="material-symbols-outlined text-[#ccc] text-[22px]">close</span>
        </button>

        {/* Botão de captura */}
        <button
          onClick={takePhoto}
          disabled={!ready}
          className="w-[68px] h-[68px] rounded-full border-[3px] border-white flex items-center justify-center cursor-pointer disabled:opacity-40 transition-transform active:scale-90"
          style={{ background: 'transparent', padding: 0 }}
          aria-label="Capturar foto"
        >
          <div className="w-[56px] h-[56px] rounded-full bg-white transition-colors active:bg-white/70" />
        </button>

        {/* Trocar câmera */}
        <button
          onClick={flipCamera}
          className="w-11 h-11 flex items-center justify-center rounded-full border-none cursor-pointer bg-white/[0.08]"
          aria-label="Trocar câmera"
        >
          <span className="material-symbols-outlined text-[#ccc] text-[22px]">flip_camera_android</span>
        </button>
      </div>
    </div>
  )
}
